import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, PencilSimple, UploadSimple, Pill } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY_FORM = { name: '', image_path: '', default_dose: '', default_indication: '', purchase_link: '', discount_code: '' };

export default function SupplementLibraryPage() {
  useRequireAdmin();
  const confirmDelete = useConfirmDelete();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);

  const fetchItems = useCallback(async () => { try { const { data } = await axios.get(`${API}/api/admin/supplement-library`); setItems(data); } catch {} }, []);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await axios.post(`${API}/api/upload`, fd);
      setForm(f => ({ ...f, image_path: data.path }));
    } catch { toast.error('Erro ao carregar imagem'); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Nome é obrigatório'); return; }
    try {
      if (editId) await axios.put(`${API}/api/admin/supplement-library/${editId}`, form);
      else await axios.post(`${API}/api/admin/supplement-library`, form);
      toast.success('Item guardado'); setShowForm(false); setEditId(null); fetchItems();
    } catch { toast.error('Erro ao guardar'); }
  };
  const del = async (id) => {
    const it = items.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o suplemento "${it?.name || ''}" da biblioteca.` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/admin/supplement-library/${id}`); toast.success('Item eliminado'); fetchItems(); } catch { toast.error('Erro'); }
  };

  return (
    <div className="fade-in" data-testid="supplement-library-page">
      <div className="flex items-center justify-between mb-5">
        <div><h2 className="font-heading font-medium text-xl text-brand-text">Biblioteca de Suplementos</h2><p className="text-xs text-brand-muted mt-0.5">Itens reutilizáveis para prescrever na ficha da paciente</p></div>
        <button onClick={() => { setEditId(null); setForm(EMPTY_FORM); setShowForm(true); }} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="new-library-item-btn"><Plus size={16} /> Novo item</button>
      </div>
      {items.length === 0 ? <p className="text-center text-brand-muted py-8">Sem itens na biblioteca.</p> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(it => (
            <div key={it.id} className="bg-white border border-brand-border rounded-2xl p-4" data-testid={`library-item-${it.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-brand-mint/15 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {it.image_path ? <img src={`${API}/api/files/${it.image_path}`} alt="" className="w-full h-full object-cover" /> : <Pill size={22} weight="duotone" className="text-brand-green" />}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-brand-text truncate">{it.name}</h3>
                  {it.default_dose && <p className="text-[11px] text-brand-muted">{it.default_dose}</p>}
                  {it.default_indication && <p className="text-[10px] text-brand-placeholder mt-0.5">{it.default_indication}</p>}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => { setEditId(it.id); setForm({ name: it.name || '', image_path: it.image_path || '', default_dose: it.default_dose || '', default_indication: it.default_indication || '', purchase_link: it.purchase_link || '', discount_code: it.discount_code || '' }); setShowForm(true); }} className="p-1 text-brand-placeholder hover:text-brand-green" data-testid={`edit-library-item-${it.id}`}><PencilSimple size={14} /></button>
                  <button onClick={() => del(it.id)} className="p-1 text-brand-placeholder hover:text-red-500" data-testid={`delete-library-item-${it.id}`}><Trash size={14} /></button>
                </div>
              </div>
              {it.purchase_link && <a href={it.purchase_link} target="_blank" rel="noreferrer" className="text-[11px] text-brand-green mt-2 block truncate">{it.purchase_link}</a>}
              {it.discount_code && <p className="text-[10px] text-brand-muted mt-0.5">Código: {it.discount_code}</p>}
            </div>
          ))}
        </div>
      )}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-3xl border-brand-border max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editId ? 'Editar item' : 'Novo item'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-xl bg-brand-bg flex items-center justify-center overflow-hidden flex-shrink-0">
                {form.image_path ? <img src={`${API}/api/files/${form.image_path}`} alt="" className="w-full h-full object-cover" /> : <Pill size={24} className="text-brand-placeholder" />}
              </div>
              <label className="text-xs text-brand-green flex items-center gap-1 cursor-pointer" data-testid="library-item-upload-btn">
                <UploadSimple size={14} /> {uploading ? 'A carregar...' : 'Carregar imagem'}
                <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && uploadImage(e.target.files[0])} disabled={uploading} />
              </label>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Nome</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-xl border-brand-border" data-testid="library-item-name" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-brand-muted mb-1 block">Dose padrão</Label><Input value={form.default_dose} onChange={e => setForm({ ...form, default_dose: e.target.value })} placeholder="Ex: 1 cápsula/dia" className="rounded-xl border-brand-border" data-testid="library-item-dose" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Código de desconto</Label><Input value={form.discount_code} onChange={e => setForm({ ...form, discount_code: e.target.value })} className="rounded-xl border-brand-border" data-testid="library-item-discount" /></div>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Indicação clínica padrão</Label><Input value={form.default_indication} onChange={e => setForm({ ...form, default_indication: e.target.value })} placeholder="Ex: Suporte imunitário" className="rounded-xl border-brand-border" data-testid="library-item-indication" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Local de compra (link)</Label><Input value={form.purchase_link} onChange={e => setForm({ ...form, purchase_link: e.target.value })} placeholder="https://..." className="rounded-xl border-brand-border" data-testid="library-item-link" /></div>
            <button onClick={save} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-library-item-btn">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
